import { useState, useEffect } from "react";
import { api, toast } from "../App";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  Download, FileSpreadsheet, FileText, Building2, Users, Clock, 
  TrendingUp, Target, CheckCircle, BarChart3, Calendar
} from "lucide-react";
import { format, subDays } from "date-fns";

const TimesheetReports = ({ user }) => {
  const [activeTab, setActiveTab] = useState("project");
  
  // Project Report State
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState("");
  const [selectedBranch, setSelectedBranch] = useState("");
  const [projectReport, setProjectReport] = useState(null);
  const [projectLoading, setProjectLoading] = useState(false);
  
  // Resource Report State
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState("");
  const [fromDate, setFromDate] = useState(format(subDays(new Date(), 30), "yyyy-MM-dd"));
  const [toDate, setToDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [resourceReport, setResourceReport] = useState(null);
  const [resourceLoading, setResourceLoading] = useState(false);

  useEffect(() => {
    fetchProjects();
    fetchUsers();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await api.get("/projects?limit=500");
      setProjects(response.data.sort((a, b) => (b.project_number || "").localeCompare(a.project_number || "")));
    } catch (error) {
      console.error("Error fetching projects:", error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get("/users");
      setUsers(response.data.sort((a, b) => {
        const nameA = `${a.first_name || ""} ${a.last_name || ""}`.trim();
        const nameB = `${b.first_name || ""} ${b.last_name || ""}`.trim();
        return nameA.localeCompare(nameB);
      }));
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const fetchProjectReport = async () => {
    if (!selectedProject) {
      toast.error("Please select a project");
      return;
    }
    
    try {
      setProjectLoading(true);
      const params = new URLSearchParams();
      if (selectedBranch) params.append("branch", selectedBranch);
      
      const response = await api.get(`/reports/timesheet/project/${selectedProject}?${params}`);
      setProjectReport(response.data);
    } catch (error) {
      console.error("Error fetching project report:", error);
      toast.error("Failed to load project report");
    } finally {
      setProjectLoading(false);
    }
  };

  const fetchResourceReport = async () => {
    if (!selectedUser) {
      toast.error("Please select a user");
      return;
    }
    if (!fromDate || !toDate) {
      toast.error("Please select date range");
      return;
    }
    
    try {
      setResourceLoading(true);
      const response = await api.get(`/reports/timesheet/resource/${selectedUser}?from_date=${fromDate}&to_date=${toDate}`);
      setResourceReport(response.data);
    } catch (error) {
      console.error("Error fetching resource report:", error);
      toast.error("Failed to load resource report");
    } finally {
      setResourceLoading(false);
    }
  };

  const handleExport = async (type, format) => {
    try {
      let url = "";
      let filename = "";
      
      if (type === "project" && projectReport) {
        const params = new URLSearchParams({ format });
        if (selectedBranch) params.append("branch", selectedBranch);
        url = `/reports/timesheet/project/${selectedProject}/export?${params}`;
        filename = `project_timesheet_${projectReport.project_number}.${format === 'excel' ? 'xlsx' : 'pdf'}`;
      } else if (type === "resource" && resourceReport) {
        url = `/reports/timesheet/resource/${selectedUser}/export?format=${format}&from_date=${fromDate}&to_date=${toDate}`;
        filename = `resource_timesheet_${resourceReport.user_name.replace(/ /g, '_')}.${format === 'excel' ? 'xlsx' : 'pdf'}`;
      } else {
        toast.error("Please generate a report first");
        return;
      }
      
      const response = await api.get(url, { responseType: 'blob' });
      const blob = new Blob([response.data]);
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success(`Exported as ${format.toUpperCase()}`);
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export report");
    }
  };

  const getEfficiencyColor = (efficiency) => {
    if (efficiency >= 100) return "text-green-600 bg-green-50";
    if (efficiency >= 80) return "text-blue-600 bg-blue-50";
    if (efficiency >= 60) return "text-yellow-600 bg-yellow-50";
    return "text-red-600 bg-red-50";
  };

  const selectedProjectData = projects.find(p => p.id === selectedProject);

  return (
    <Layout user={user}>
      <div className="space-y-6 p-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-md3-on-surface">Timesheet Reports</h1>
            <p className="text-md3-on-surface-variant">Analyze timesheet data by project or resource</p>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full max-w-md grid-cols-2">
            <TabsTrigger value="project" className="flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              Project Report
            </TabsTrigger>
            <TabsTrigger value="resource" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Resource Report
            </TabsTrigger>
          </TabsList>

          {/* Project Report Tab */}
          <TabsContent value="project" className="space-y-6">
            {/* Filters */}
            <Card className="bg-md3-surface-container shadow-md3-1">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Select Project</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap items-end gap-4">
                  <div className="w-72">
                    <Label className="text-sm mb-1 block">Project</Label>
                    <Select value={selectedProject} onValueChange={(val) => {
                      setSelectedProject(val);
                      setSelectedBranch("");
                      setProjectReport(null);
                    }}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a project..." />
                      </SelectTrigger>
                      <SelectContent>
                        {projects.map(proj => (
                          <SelectItem key={proj.id} value={proj.id}>
                            {proj.project_number} - {proj.name?.substring(0, 30)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {selectedProjectData && selectedProjectData.branch?.length > 1 && (
                    <div className="w-48">
                      <Label className="text-sm mb-1 block">Branch Filter</Label>
                      <Select value={selectedBranch} onValueChange={setSelectedBranch}>
                        <SelectTrigger>
                          <SelectValue placeholder="All Branches" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Branches</SelectItem>
                          {selectedProjectData.branch.map(branch => (
                            <SelectItem key={branch} value={branch}>{branch}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  
                  <Button onClick={fetchProjectReport} disabled={projectLoading || !selectedProject}>
                    {projectLoading ? "Loading..." : "Generate Report"}
                  </Button>
                  
                  {projectReport && (
                    <div className="flex gap-2 ml-auto">
                      <Button variant="outline" size="sm" onClick={() => handleExport("project", "excel")}>
                        <FileSpreadsheet className="h-4 w-4 mr-1" />
                        Excel
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleExport("project", "pdf")}>
                        <FileText className="h-4 w-4 mr-1" />
                        PDF
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Project Report Results */}
            {projectReport && (
              <>
                {/* Project Info */}
                <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
                  <CardContent className="pt-4">
                    <div className="flex flex-wrap items-center justify-between gap-4">
                      <div>
                        <h2 className="text-xl font-bold text-blue-900">{projectReport.project_number}</h2>
                        <p className="text-blue-700">{projectReport.project_name}</p>
                        <p className="text-sm text-blue-600">Client: {projectReport.client_name || "N/A"}</p>
                      </div>
                      {projectReport.selected_branch && (
                        <Badge variant="outline" className="bg-blue-100 text-blue-700">
                          Branch: {projectReport.selected_branch}
                        </Badge>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
                  <Card className="bg-slate-50 border-slate-200">
                    <CardContent className="pt-4 text-center">
                      <Clock className="h-5 w-5 mx-auto text-slate-600 mb-1" />
                      <p className="text-xs text-slate-600">Base Allocated</p>
                      <p className="text-lg font-bold text-slate-800">{projectReport.summary.base_allocated_hours}h</p>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-purple-50 border-purple-200">
                    <CardContent className="pt-4 text-center">
                      <FileText className="h-5 w-5 mx-auto text-purple-600 mb-1" />
                      <p className="text-xs text-purple-600">CO Hours</p>
                      <p className="text-lg font-bold text-purple-800">+{projectReport.summary.co_approved_hours}h</p>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-blue-50 border-blue-200">
                    <CardContent className="pt-4 text-center">
                      <Target className="h-5 w-5 mx-auto text-blue-600 mb-1" />
                      <p className="text-xs text-blue-600">Total Allocated</p>
                      <p className="text-lg font-bold text-blue-800">{projectReport.summary.total_allocated_hours}h</p>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-orange-50 border-orange-200">
                    <CardContent className="pt-4 text-center">
                      <Clock className="h-5 w-5 mx-auto text-orange-600 mb-1" />
                      <p className="text-xs text-orange-600">Actual Hours</p>
                      <p className="text-lg font-bold text-orange-800">{projectReport.summary.total_actual_hours}h</p>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-green-50 border-green-200">
                    <CardContent className="pt-4 text-center">
                      <CheckCircle className="h-5 w-5 mx-auto text-green-600 mb-1" />
                      <p className="text-xs text-green-600">Earned Hours</p>
                      <p className="text-lg font-bold text-green-800">{projectReport.summary.total_earned_hours}h</p>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-cyan-50 border-cyan-200">
                    <CardContent className="pt-4 text-center">
                      <BarChart3 className="h-5 w-5 mx-auto text-cyan-600 mb-1" />
                      <p className="text-xs text-cyan-600">% Completion</p>
                      <p className="text-lg font-bold text-cyan-800">{projectReport.summary.completion_percentage}%</p>
                    </CardContent>
                  </Card>
                  
                  <Card className={`${getEfficiencyColor(projectReport.summary.efficiency_percentage)} border`}>
                    <CardContent className="pt-4 text-center">
                      <TrendingUp className="h-5 w-5 mx-auto mb-1" />
                      <p className="text-xs">% Efficiency</p>
                      <p className="text-lg font-bold">{projectReport.summary.efficiency_percentage}%</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Approved Change Orders */}
                {projectReport.approved_change_orders?.length > 0 && (
                  <Card className="bg-md3-surface-container shadow-md3-1">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base">Approved Change Orders ({projectReport.approved_change_orders.length})</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-wrap gap-2">
                        {projectReport.approved_change_orders.map(co => (
                          <Badge key={co.id} variant="outline" className="bg-purple-50 text-purple-700">
                            {co.change_order_number}: +{co.approved_hours}h
                          </Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Resources Table */}
                <Card className="bg-md3-surface-container shadow-md3-1">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Resources ({projectReport.resources?.length || 0})</CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-md3-surface-container-high">
                            <TableHead>Resource Name</TableHead>
                            <TableHead>Department</TableHead>
                            <TableHead>Branch</TableHead>
                            <TableHead className="text-right">Actual Hours</TableHead>
                            <TableHead className="text-right">Earned Hours</TableHead>
                            <TableHead className="text-right">Efficiency %</TableHead>
                            <TableHead className="text-right">Timesheets</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {projectReport.resources?.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={7} className="text-center py-8 text-md3-on-surface-variant">
                                No timesheet entries found for this project
                              </TableCell>
                            </TableRow>
                          ) : (
                            projectReport.resources?.map((resource) => (
                              <TableRow key={resource.user_id} className="hover:bg-md3-surface-container-high">
                                <TableCell className="font-medium">{resource.user_name}</TableCell>
                                <TableCell>
                                  <Badge variant="outline" className="text-xs">{resource.department}</Badge>
                                </TableCell>
                                <TableCell>{resource.branch}</TableCell>
                                <TableCell className="text-right">{resource.actual_hours}</TableCell>
                                <TableCell className="text-right">{resource.earned_hours}</TableCell>
                                <TableCell className="text-right">
                                  <span className={`px-2 py-1 rounded text-sm ${getEfficiencyColor(resource.efficiency)}`}>
                                    {resource.efficiency}%
                                  </span>
                                </TableCell>
                                <TableCell className="text-right">{resource.timesheet_count}</TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>

          {/* Resource Report Tab */}
          <TabsContent value="resource" className="space-y-6">
            {/* Filters */}
            <Card className="bg-md3-surface-container shadow-md3-1">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Select Resource & Date Range</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap items-end gap-4">
                  <div className="w-64">
                    <Label className="text-sm mb-1 block">Resource</Label>
                    <Select value={selectedUser} onValueChange={(val) => {
                      setSelectedUser(val);
                      setResourceReport(null);
                    }}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a user..." />
                      </SelectTrigger>
                      <SelectContent>
                        {users.map(u => (
                          <SelectItem key={u.id} value={u.id}>
                            {u.first_name} {u.last_name} ({u.department})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="w-40">
                    <Label className="text-sm mb-1 block">From Date</Label>
                    <Input 
                      type="date" 
                      value={fromDate} 
                      onChange={(e) => setFromDate(e.target.value)}
                    />
                  </div>
                  
                  <div className="w-40">
                    <Label className="text-sm mb-1 block">To Date</Label>
                    <Input 
                      type="date" 
                      value={toDate} 
                      onChange={(e) => setToDate(e.target.value)}
                    />
                  </div>
                  
                  <Button onClick={fetchResourceReport} disabled={resourceLoading || !selectedUser}>
                    {resourceLoading ? "Loading..." : "Generate Report"}
                  </Button>
                  
                  {resourceReport && (
                    <div className="flex gap-2 ml-auto">
                      <Button variant="outline" size="sm" onClick={() => handleExport("resource", "excel")}>
                        <FileSpreadsheet className="h-4 w-4 mr-1" />
                        Excel
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleExport("resource", "pdf")}>
                        <FileText className="h-4 w-4 mr-1" />
                        PDF
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Resource Report Results */}
            {resourceReport && (
              <>
                {/* Resource Info */}
                <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
                  <CardContent className="pt-4">
                    <div className="flex flex-wrap items-center justify-between gap-4">
                      <div>
                        <h2 className="text-xl font-bold text-green-900">{resourceReport.user_name}</h2>
                        <p className="text-green-700">Department: {resourceReport.department}</p>
                        <p className="text-sm text-green-600">Branch: {resourceReport.branch}</p>
                      </div>
                      <div className="text-right">
                        <Badge variant="outline" className="bg-green-100 text-green-700">
                          <Calendar className="h-3 w-3 mr-1" />
                          {resourceReport.date_range.from_date} to {resourceReport.date_range.to_date}
                        </Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <Card className="bg-orange-50 border-orange-200">
                    <CardContent className="pt-4 text-center">
                      <Clock className="h-5 w-5 mx-auto text-orange-600 mb-1" />
                      <p className="text-xs text-orange-600">Total Actual</p>
                      <p className="text-xl font-bold text-orange-800">{resourceReport.summary.total_actual_hours}h</p>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-green-50 border-green-200">
                    <CardContent className="pt-4 text-center">
                      <CheckCircle className="h-5 w-5 mx-auto text-green-600 mb-1" />
                      <p className="text-xs text-green-600">Total Earned</p>
                      <p className="text-xl font-bold text-green-800">{resourceReport.summary.total_earned_hours}h</p>
                    </CardContent>
                  </Card>
                  
                  <Card className={`${getEfficiencyColor(resourceReport.summary.overall_efficiency)} border`}>
                    <CardContent className="pt-4 text-center">
                      <TrendingUp className="h-5 w-5 mx-auto mb-1" />
                      <p className="text-xs">Efficiency</p>
                      <p className="text-xl font-bold">{resourceReport.summary.overall_efficiency}%</p>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-blue-50 border-blue-200">
                    <CardContent className="pt-4 text-center">
                      <Building2 className="h-5 w-5 mx-auto text-blue-600 mb-1" />
                      <p className="text-xs text-blue-600">Projects</p>
                      <p className="text-xl font-bold text-blue-800">{resourceReport.summary.project_count}</p>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-purple-50 border-purple-200">
                    <CardContent className="pt-4 text-center">
                      <FileText className="h-5 w-5 mx-auto text-purple-600 mb-1" />
                      <p className="text-xs text-purple-600">Timesheets</p>
                      <p className="text-xl font-bold text-purple-800">{resourceReport.summary.total_timesheets}</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Projects Table */}
                <Card className="bg-md3-surface-container shadow-md3-1">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Projects Worked On ({resourceReport.projects?.length || 0})</CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-md3-surface-container-high">
                            <TableHead>Project #</TableHead>
                            <TableHead>Project Name</TableHead>
                            <TableHead>Client</TableHead>
                            <TableHead className="text-right">Actual Hours</TableHead>
                            <TableHead className="text-right">Earned Hours</TableHead>
                            <TableHead className="text-right">Efficiency %</TableHead>
                            <TableHead className="text-right">Work Days</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {resourceReport.projects?.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={7} className="text-center py-8 text-md3-on-surface-variant">
                                No timesheet entries found for this period
                              </TableCell>
                            </TableRow>
                          ) : (
                            resourceReport.projects?.map((proj) => (
                              <TableRow key={proj.project_id} className="hover:bg-md3-surface-container-high">
                                <TableCell className="font-medium">{proj.project_number}</TableCell>
                                <TableCell>{proj.project_name}</TableCell>
                                <TableCell>{proj.client_name}</TableCell>
                                <TableCell className="text-right">{proj.actual_hours}</TableCell>
                                <TableCell className="text-right">{proj.earned_hours}</TableCell>
                                <TableCell className="text-right">
                                  <span className={`px-2 py-1 rounded text-sm ${getEfficiencyColor(proj.efficiency)}`}>
                                    {proj.efficiency}%
                                  </span>
                                </TableCell>
                                <TableCell className="text-right">{proj.work_days}</TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default TimesheetReports;
